import * as Actions from './label-que.actions';
import { SAVE_OR_EDIT_BATCH_NOTE_SUCCESS } from '../batches/notes/batch-note.actions';
import moment from 'moment';
import { APP_INITIALIZATION } from '../core/base-actions';

const initialState = {
  isOpen: false,
  batches: [],
  selectedBatches: [],
  previousBatches: [],
  fromProcessDate: moment().add(-3, 'days').format('MM/DD/YYYY'),
  pageSize: 25,
  isLoading: true,
  errorsAndWarnings: [],
  isLabelFetchComplete: false,
};

const getSelectedBatches = (state, batchId) => {
  const selectedBatches = [...state.selectedBatches];
  if (selectedBatches.includes(batchId)) {
    return selectedBatches.filter((bId) => bId !== batchId);
  }
  selectedBatches.push(batchId);
  return selectedBatches;
};

const getUpdatedBatches = (state, labelQueBatch) => {
  const { batches } = state;
  const batchToAdd = {
    ...labelQueBatch,
    batchId: labelQueBatch.batchId.toString(),
  };
  const foundBatchIndex = batches.findIndex((batch) => batch.batchId === batchToAdd.batchId);

  if (foundBatchIndex !== -1) {
    return [...batches.slice(0, foundBatchIndex), batchToAdd, ...batches.slice(foundBatchIndex + 1)];
  }
  return [batchToAdd, ...batches];
};

const returnUpdatedPageSize = (state) => {
  let { pageSize } = state;
  return {
    ...state,
    pageSize: (pageSize += 25),
    previousBatches: state.batches,
    fromProcessDate: undefined,
  };
};

const setLoading = (state, isLoading) => ({
  ...state,
  isLoading,
});

const setLoadingAndMarkBatchesForExclusion = (state, isLoading) => ({
  ...state,
  isLoading,
  batches: state.batches.map((batch) => ({
    ...batch,
    isMarkedForExclusion: state.selectedBatches.includes(batch.batchId),
  })),
});

const getBatchesAndResetSelected = (state, batches) => {
  const { selectedBatches } = state;
  return {
    ...state,
    batches: batches.map((batch) => ({ ...batch, isComplete: true })),
    selectedBatches: selectedBatches.filter((selectedBatchId) => batches.find((batch) => batch.batchId === selectedBatchId)),
    isLoading: false,
    isLabelFetchComplete: state.previousBatches.length === batches.length,
  };
};

const initialFormOfBatchToErrorOrWarning = (batchId) => ({
  batchId,
  isLoading: true,
  items: [],
  isExpanded: true,
});

const updatedFormOfBatchToErrorOrWarning = (batchId, response) => ({
  batchId,
  isLoading: false,
  items: response,
  isExpanded: true,
});

const addOrClearErrorOrWarnState = (state, batchId) => {
  const { errorsAndWarnings } = state;
  const foundObj = errorsAndWarnings.find((errOrWarn) => errOrWarn.batchId === batchId);
  if (!foundObj) {
    return [...errorsAndWarnings, initialFormOfBatchToErrorOrWarning(batchId)];
  }
  return [...errorsAndWarnings.filter((errOrWarn) => errOrWarn.batchId !== batchId), initialFormOfBatchToErrorOrWarning(batchId)];
};

const updateErrorOrWarnState = (state, response) => {
  if (!response || !response.length) {
    return [];
  }
  const batchId = response[0].labelBatchId;
  const { errorsAndWarnings } = state;
  const foundObj = errorsAndWarnings.find((errOrWarn) => errOrWarn.batchId === batchId);
  if (!foundObj) {
    return [...errorsAndWarnings, updatedFormOfBatchToErrorOrWarning(batchId, response)];
  }
  const updatedArray = errorsAndWarnings.filter((errOrWarn) => errOrWarn.batchId !== batchId);
  updatedArray.push(updatedFormOfBatchToErrorOrWarning(batchId, response));
  return updatedArray;
};

const collapseErrorOrWarning = (state, batchId) => {
  const { errorsAndWarnings } = state;
  return {
    ...state,
    errorsAndWarnings: errorsAndWarnings.map((err) => {
      return err.batchId !== batchId ? { ...err } : { ...err, isExpanded: false };
    }),
  };
};

const resetRetriedBatch = (state, batchId) => {
  const stateBatches = state.batches;
  const batchIndex = stateBatches.findIndex((batch) => batch.batchId === batchId);
  const batch = stateBatches[batchIndex];
  const resetBatchStatus = {
    batchId: batch.batchId,
    batchNumber: batch.batchNumber,
    total: batch.total,
  };

  return {
    ...state,
    batches: [...stateBatches.slice(0, batchIndex), resetBatchStatus, ...stateBatches.slice(batchIndex + 1)],
    errorsAndWarnings: state.errorsAndWarnings.filter((errWarn) => errWarn.batchId !== batchId),
  };
};

const updateBatchNote = (state, { batchId, note }) => {
  const matchingBatchIndex = state.batches.findIndex((b) => b.batchId === batchId);
  if (matchingBatchIndex < 0) {
    return state;
  }
  const updatedBatch = {
    ...state.batches[matchingBatchIndex],
    batchNotes: note,
  };
  return {
    ...state,
    batches: [...state.batches.slice(0, matchingBatchIndex), updatedBatch, ...state.batches.slice(matchingBatchIndex + 1)],
  };
};

const actionTypeToReducerMap = {
  [Actions.TOGGLE_LABEL_QUE]: (state, { isOpen }) => ({
    ...state,
    isOpen,
  }),
  [Actions.SET_LABEL_QUE_BATCH_SELECTED]: (state, { batchId }) => ({
    ...state,
    selectedBatches: getSelectedBatches(state, batchId),
  }),
  [Actions.SELECT_ALL_LABEL_QUE_BATCHES]: (state) => ({
    ...state,
    selectedBatches: state.batches.map((batch) => batch.batchId),
  }),
  [Actions.DESELECT_ALL_LABEL_QUE_BATCHES]: (state) => ({
    ...state,
    selectedBatches: [],
  }),
  [Actions.ADD_LABEL_BATCH_TO_LABEL_QUE]: (state, { labelQueBatch }) => ({
    ...state,
    batches: getUpdatedBatches(state, labelQueBatch),
  }),
  [Actions.LOAD_MORE_LABEL_QUE_BATCHES]: (state) => {
    return returnUpdatedPageSize(state);
  },
  [Actions.LABEL_QUE_GET_BATCHES_REQUEST]: (state) => setLoading(state, true),
  [APP_INITIALIZATION]: (state, { initialData }) => getBatchesAndResetSelected(state, initialData?.labelQueBatches),
  [Actions.GET_LABEL_QUE_BATCHES_SUCCESS]: (state, { batches }) => getBatchesAndResetSelected(state, batches),
  [Actions.GET_LABEL_QUE_BATCHES_FAILURE]: (state) => setLoading(state, false),
  [Actions.LABEL_QUE_ARCHIVE_BATCHES_REQUEST]: (state) => setLoadingAndMarkBatchesForExclusion(state, true),
  [Actions.LABEL_QUE_ARCHIVE_BATCHES_SUCCESS]: (state) => setLoading(state, false),
  [Actions.LABEL_QUE_ARCHIVE_BATCHES_FAILURE]: (state) => setLoading(state, false),
  [Actions.LABEL_QUE_GET_WARNING_AND_ERROR_REQUEST]: (state, { batchId }) => ({
    ...state,
    errorsAndWarnings: addOrClearErrorOrWarnState(state, batchId),
  }),
  [Actions.LABEL_QUE_GET_WARNING_AND_ERROR_SUCCESS]: (state, { response }) => ({
    ...state,
    errorsAndWarnings: updateErrorOrWarnState(state, response),
  }),
  [Actions.COLLAPSE_ERROR_OR_WARNING]: (state, { batchId }) => collapseErrorOrWarning(state, batchId),
  [Actions.ON_RETRY_BATCH_REQUEST]: (state, { batchId }) => resetRetriedBatch(state, batchId),
  [SAVE_OR_EDIT_BATCH_NOTE_SUCCESS]: (state, action) => updateBatchNote(state, action),
};

export const reduce = (state = initialState, action) => {
  const reducer = actionTypeToReducerMap[action.type];
  if (!reducer) {
    return state;
  }
  return reducer(state, action);
};

export { namespace } from './label-que.reducer.metadata';
